import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpenseForm } from 'src/Components/Forms/Expense/ExpenseForm';
import { ExtraPayerOption } from 'src/Components/Forms/Expense/ExpenseForm/ExpenseForm.types';
import { EXPENSE_TYPE_TREE, MainCategory } from 'src/Constants/expenseCategorization';
import { defaultRecurrenceValue } from 'src/Hooks/Forms/UseCreateEventForm/useCreateEventForm';
import {
  CreateExpenseFormSplitScope,
  useCreateExpenseForm,
} from 'src/Hooks/Forms/useCreateExpenseForm';
import { usePayerError } from 'src/Hooks/Forms/usePayerError';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import { useUpdateExpense } from 'src/Hooks/Mutations/useUpdateExpense';
import useFetchExpense from 'src/Hooks/Queries/useFetchExpense';
import useFetchMembers from 'src/Hooks/Queries/useFetchMembers';
import {
  CreateExpenseDtoCurrencyEnum,
  CreateExpenseDtoSplitTypeEnum,
  CreateExpenseDtoTypeEnum,
  RecurrenceDtoFrequencyEnum,
} from 'src/Services/API/generated';

import { Props } from './ExpenseEditionFormWrapper.types';

const ExpenseEditionFormWrapper = ({ expenseId, onCancel, onCreate }: Props) => {
  const { data: expense } = useFetchExpense(expenseId);
  const { form } = useCreateExpenseForm();
  const handlePayerError = usePayerError(form);
  const { mutate } = useUpdateExpense({
    onSuccess: () => onCreate(),
    onError: handlePayerError,
  });
  const { t } = useTranslation();
  const { data: members } = useFetchMembers();
  const { mutateAsync: fetchFileInfo } = useFetchStorageItemInformation();

  // Edge case 4: the placeholder recorded as payer may have been removed from the members list
  // since (merged, deleted). We surface it as an extra option so edition still shows the label.
  const extraPayerOptions = useMemo<ExtraPayerOption[] | undefined>(() => {
    const placeholder = expense?.paidByPlaceholderMember;
    if (!placeholder) return undefined;
    const isKnown = members?.members.some(m => m.user.id === placeholder.id);
    if (isKnown) return undefined;
    return [
      {
        label: `${placeholder.firstName} ${placeholder.lastName}`,
        value: `placeholder-${placeholder.id}`,
      },
    ];
  }, [expense?.paidByPlaceholderMember, members]);

  useEffect(() => {
    const populateForm = async () => {
      if (expense) {
        // A legacy row can have no payer recorded at all; keep paidBy undefined so validation
        // catches it instead of producing a `user-undefined` value.
        const paidByValue = expense.paidByBankAccount
          ? `bank-${expense.paidByBankAccount.id}`
          : expense.paidByContact
            ? `contact-${expense.paidByContact.id}`
            : expense.paidByPlaceholderMember
              ? `placeholder-${expense.paidByPlaceholderMember.id}`
              : expense.paidBy?.id
                ? `user-${expense.paidBy.id}`
                : undefined;

        const reimburseToValue = expense.reimburseToBankAccount
          ? `bank-${expense.reimburseToBankAccount.id}`
          : '';

        const recurrenceOptions = expense.recurrenceOptions;

        // A fund call is only treated as such in edition when it still has a single share:
        // historical multi-share data must stay in classic mode to avoid silently overwriting it.
        const isFundCallType =
          EXPENSE_TYPE_TREE[expense.type as CreateExpenseDtoTypeEnum]?.main ===
          MainCategory.FundingCalls;
        // A fund call payer is always a registered member: shares borne by an unregistered
        // co-owner never qualify.
        const fundCallPayerId =
          isFundCallType && expense.shares.length === 1 ? expense.shares[0].user?.id : undefined;

        form.setValues({
          title: expense.title,
          merchant: expense.merchant,
          note: expense.note ?? '',
          amount: expense.amount,
          currency: expense.currency as CreateExpenseDtoCurrencyEnum,
          type: expense.type as CreateExpenseDtoTypeEnum,
          paidBy: paidByValue,
          fundCallPayerId,
          paidAt: expense.paidAt.toDate(),
          isIncome: expense.isIncome,
          splitType: expense.splitType as CreateExpenseDtoSplitTypeEnum,
          splitScope:
            ((expense as unknown as { splitScope?: string }).splitScope as
              | CreateExpenseFormSplitScope
              | undefined) ?? CreateExpenseFormSplitScope.INDIVIDUAL,
          reimburseToBankAccount: reimburseToValue,
          locativeShare: expense.locativeShare,
          recurrence: recurrenceOptions
            ? {
                ...defaultRecurrenceValue,
                frequency: recurrenceOptions.frequency as RecurrenceDtoFrequencyEnum,
                interval: String(recurrenceOptions.interval ?? 1),
                byMonthDays: recurrenceOptions.byMonthDay?.map(String) ?? [],
                byMonth: recurrenceOptions.byMonth ?? [],
                byWeekDay: String(recurrenceOptions.byWeekDay?.[0] ?? ''),
                byWeekDays: recurrenceOptions.byWeekDay?.map(String) ?? [],
                byMonthDay: String(recurrenceOptions.bySetPos ?? 1),
                until: recurrenceOptions.until ? recurrenceOptions.until.toDate() : undefined,
                timezone: recurrenceOptions.timezone,
              }
            : defaultRecurrenceValue,
          shares:
            members?.members.map(member => {
              // A share carries either a user or a placeholder member; both are matched on the
              // id the members endpoint exposes under `user.id`.
              const result = expense.shares.find(
                s => (s.user?.id ?? s.placeholderMember?.id) === member.user.id
              );
              return {
                selected: !!result,
                participantId: member.user.id,
                isPlaceholder: member.isPlaceholder,
                amount: result?.amount ?? 0,
                percentage: result?.percentage ?? 0,
              };
            }) ?? [],
        });

        if (expense.attachments && expense.attachments.length > 0) {
          try {
            const attachmentInfoPromises = expense.attachments.map(attachment =>
              fetchFileInfo(attachment.id)
            );

            const resolvedFileInfos = await Promise.all(attachmentInfoPromises);

            const formAttachments = expense.attachments
              .map(attachment => {
                const fileInfo = resolvedFileInfos.find(info => info.id === attachment.id);
                if (!fileInfo) return undefined;
                return {
                  fileId: attachment.id,
                  name: attachment.name,
                  previewUrl: fileInfo?.urls?.previewThumbnailUrl,
                  type: fileInfo.type,
                  video: fileInfo.video,
                };
              })
              .filter(attachment => attachment !== undefined);

            form.setFieldValue('attachments', formAttachments);
          } catch (error) {
            console.error('Failed to fetch attachment information:', error);
          }
        }
      }
    };

    populateForm();
  }, [expense, members]);

  return (
    <ExpenseForm
      onCancel={onCancel}
      form={form}
      isEdition
      extraPayerOptions={extraPayerOptions}
      onSubmit={form.onSubmit(values => {
        mutate({ expenseId, updateData: values });
      })}
      submitButtonLabel={t('expenses.editExpense', 'Modifier')}
    />
  );
};

export default ExpenseEditionFormWrapper;
